/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useEffect, useRef, useState, useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import type { DocNode } from '@atlaskit/adf-schema';

import { ConvoAIResponseState, useConvoAIStreaming } from '@atlassian/ai-agent-streaming';
import type {
	AiFeatureRequestEnvelopeSuggestIssuesDocumentationRequest,
	FinalResponseMessage,
} from '@atlassian/convo-ai-api';
import { markdownPlusToAdf, adfToMarkdownPlus } from '@atlassian/adf-markdown-transformer';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import type { AiIssueCreateState } from '@atlassian/ai-issue-create';
import { useConfluencePageAri } from '@atlassian/ai-summary';

import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { fg } from '@confluence/feature-gating';

import { useBuildIssueSuggestionsRequest } from '../utils/useBuildIssueSuggestionsRequest';
import type { Issue, JiraAIResponse } from '../__types__/AiAgentStreamingTypes';
import { AiStates, useBulkCreateContext } from '../providers/BulkCreateContextProvider';
import { useBulkCreateLoggingEvents } from '../utils/useBulkCreateLoggingEvents';
import type {
	useAIAgentStreamingDataBulkIssueADFQuery as useConvoAIIssuesStreamingDataBulkIssueADFQuery$data,
	useAIAgentStreamingDataBulkIssueADFQueryVariables as useConvoAIIssuesStreamingDataBulkIssueADFQuery$variables,
} from '../services/__types__/useAIAgentStreamingDataBulkIssueADFQuery';
import { useAiIssueCreationEnablementState } from '../providers/IssueCreateSidePanelContextProvider';

export const useConvoAIIssuesStreaming = (isConvoAIMigrationEnabled: boolean) => {
	const [
		{ bulkIssuesList, isCreatedFromActionItems, isCreatedFromTable, expectedIssueCount },
		{ setBulkIssuesList },
	] = useBulkCreateContext();
	const aiIssueCreationEnablement = useAiIssueCreationEnablementState();
	const [markdownPlusContent, setMarkdownPlusContent] = useState('');
	const [pageIdMap, setPageIdMap] = useState<IDMap | undefined>(undefined);
	const [currentAiState, setCurrentAiState] = useState<AiIssueCreateState>(AiStates.LOADING);
	const { logAiStreamingBackendFailed, logAiNoResultsGenerated, logAiInvalidResultsGenerated } =
		useBulkCreateLoggingEvents();
	const { cloudId } = useSessionData();
	const [contentType] = useContentType();
	const [contentId] = usePageContentId();

	const pageId = contentId ?? '';
	const pageType = contentType ?? '';

	const { pageAri } = useConfluencePageAri(cloudId, pageId, pageType);

	// Used to keep track of the issues that have been added to the list and give a unique id
	const issueIndex = useRef(0);
	const isRefetched = useRef(false);

	// Needed to convert confluence content into MD+ format for the ai request
	useQuery<
		useConvoAIIssuesStreamingDataBulkIssueADFQuery$data,
		useConvoAIIssuesStreamingDataBulkIssueADFQuery$variables
	>(
		graphql`
			query useConvoAIIssuesStreamingDataBulkIssueADFQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					body {
						atlas_doc_format {
							value
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId: pageId,
			},
			onError: (_adfError) => {
				setCurrentAiState(AiStates.ERROR);
			},
			fetchPolicy: 'network-only',
			onCompleted: (adfData) => {
				const adf = adfData?.singleContent?.body?.atlas_doc_format?.value;
				if (adf) {
					try {
						const parsedAdf: DocNode = JSON.parse(adf);
						const { markdown: markdownPlus, idMap } = adfToMarkdownPlus(parsedAdf);
						// Handle pages that don't have any content
						if (!markdownPlus) {
							setCurrentAiState(AiStates.ERROR);
							return;
						}
						setMarkdownPlusContent(markdownPlus);
						setPageIdMap(idMap);
					} catch (err) {
						setCurrentAiState(AiStates.ERROR);
					}
				}
			},
			skip: currentAiState !== AiStates.LOADING || !isConvoAIMigrationEnabled,
		},
	);

	const [request, config] = useBuildIssueSuggestionsRequest(
		markdownPlusContent,
		pageAri ?? '',
		isConvoAIMigrationEnabled,
		...(fg('confluence-create-work-items-from-action-items')
			? [aiIssueCreationEnablement?.enablement]
			: []),
	);

	const [{ content, responseState, error }, refetch] = useConvoAIStreaming<
		AiFeatureRequestEnvelopeSuggestIssuesDocumentationRequest,
		FinalResponseMessage
	>(request, config);

	useEffect(() => {
		if (
			markdownPlusContent &&
			responseState !== ConvoAIResponseState.FinalResponse &&
			!isRefetched.current
		) {
			refetch();
			isRefetched.current = true;
		}
	}, [markdownPlusContent, responseState, refetch]);

	const issues = useMemo(() => {
		try {
			return content
				? (JSON.parse(content) as JiraAIResponse)
				: { suggested_issues: [] as Issue[] };
		} catch {
			return { suggested_issues: [] as Issue[] };
		}
	}, [content]);

	useEffect(() => {
		const getIssueDetails = () => {
			return {
				iconUrl: '',
				name: '',
				id: '',
			};
		};

		// Temp add issues until we can handle partial responses
		const addIssues = (issueList: Issue[]) => {
			const newIssues = issueList.map((issue) => ({
				id: String(issueIndex.current++),
				summary: issue.field_values.Summary,
				description: markdownPlusToAdf({
					markdown: issue.field_values.Description,
					idMap: pageIdMap,
				}),
				issueType: getIssueDetails(),
			}));

			void setBulkIssuesList(newIssues);
		};

		// Switch statement to handle the different response states
		switch (responseState) {
			case ConvoAIResponseState.Initialization:
				// Not needed really just want to track all the states
				void setBulkIssuesList([]);
				break;
			case ConvoAIResponseState.AnswerPart:
				// Cleaning up the FG for now - will update at a later time with a semi-loadin state for chunk responses
				break;
			case ConvoAIResponseState.FinalResponse:
				try {
					if (issues?.suggested_issues?.length === 0) {
						logAiNoResultsGenerated();
						setCurrentAiState(AiStates.ERROR);
					} else if (
						(isCreatedFromTable || isCreatedFromActionItems) &&
						issues?.suggested_issues?.length !== expectedIssueCount &&
						fg('confluence-create-work-items-from-action-items')
					) {
						// Unable to create a 1:1 mapping of action items to issues
						logAiInvalidResultsGenerated();
						setCurrentAiState(AiStates.ERROR);
					} else {
						// Setting list when all issues are returned for now, will update to handle partial responses
						addIssues(issues?.suggested_issues);
						setCurrentAiState(AiStates.IDLE);
					}
				} catch (e) {
					setCurrentAiState(AiStates.ERROR);
					logAiStreamingBackendFailed(e);
				}
				break;
			case ConvoAIResponseState.Error:
				setCurrentAiState(AiStates.ERROR);
				logAiStreamingBackendFailed(error);
				break;
			default:
				break;
		}
	}, [
		issues,
		pageIdMap,
		responseState,
		issueIndex,
		error,
		content,
		isCreatedFromActionItems,
		isCreatedFromTable,
		expectedIssueCount,
		logAiInvalidResultsGenerated,
		logAiStreamingBackendFailed,
		logAiNoResultsGenerated,
		setBulkIssuesList,
	]);

	return {
		convoAIContent: bulkIssuesList,
		convoAICurrentAiState: currentAiState,
		convoAIResponseState: responseState,
	};
};
